/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Grid, Typography } from '@mui/material';
import React from 'react';
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux';
import { getQueryClient } from '@opengeoweb/api';
import {
  GEOWEB_ROLE_PRESETS_ADMIN,
  useAuthenticationContext,
} from '@opengeoweb/authentication';
import type { InitialProps } from '@opengeoweb/core';
import {
  CustomToggleButton,
  CustomTooltip,
  renderCounter,
} from '@opengeoweb/shared';
import { snackbarActions, snackbarTypes } from '@opengeoweb/snackbar';
import type { SoundingsModuleState } from '@opengeoweb/soundings';
import { soundingsSelectors } from '@opengeoweb/soundings';
import type { mapTypes } from '@opengeoweb/store';
import {
  mapSelectors,
  syncGroupsSelectors,
  constructSyncSourceDisplayName,
  mapActions,
  uiSelectors,
  uiTypes,
} from '@opengeoweb/store';
import type { TimeSeriesModuleState } from '@opengeoweb/time-series';
import { timeSeriesSelectors } from '@opengeoweb/time-series';
import type { WarningModuleStore } from '@opengeoweb/warnings';
import { publicWarningsSelectors } from '@opengeoweb/warnings';
import { viewpresetQueryOptions } from '../../api/viewpreset-api/queryOptions';
import type { AppStore } from '../../store/types';
import {
  viewPresetActions,
  viewPresetSelectors,
} from '../../store/viewPresets';
import type {
  ViewPreset,
  ViewPresetDialog,
} from '../../store/viewPresets/types';
import { PresetAction } from '../../store/viewPresets/types';
import { emptyViewPreset } from '../../store/viewPresets/utils';
import { workspaceActions } from '../../store/workspace';
import {
  getViewById,
  getWorkspaceState,
  getViewComponentType,
  getViewPresetToSendToBackend,
} from '../../store/workspace/selectors';
import type { WorkspaceViewType } from '../../store/workspace/types';
import { WorkspacePresetAction } from '../../store/workspace/types';
import { workspaceListActions } from '../../store/workspaceList';
import {
  sanitizeViewPresetForClipboard,
  writeToClipboard,
} from '../../utils/clipboardUtils';
import { useWorkspaceTranslation } from '../../utils/i18n';
import { getPresetTitle } from '../../utils/utils';
import { PastePresetDialog } from '../PastePresetDialog';
import { viewPresetOptionId } from '../WorkspaceMenu/WorkspaceMenuButton';
import { WorkspaceOptions } from '../WorkspaceOptions/WorkspaceOptions';
import { LoadingIndicatorConnect } from '../WorkspaceView/LoadingIndicator/LoadingIndicatorConnect';

export interface ToggleViewPresetDialogProps {
  panelId: string;
  isViewPresetDialogOpen: boolean;
}

interface WorkspaceViewTitleConnectProps {
  panelId: string;
  isViewPresetDialogOpen: boolean;
}

const WorkspaceViewTitleConnectNoMemo: React.FC<
  WorkspaceViewTitleConnectProps
> = ({ panelId, isViewPresetDialogOpen }: WorkspaceViewTitleConnectProps) => {
  const dispatch = useDispatch();
  const store = useStore<AppStore>();
  const { t } = useWorkspaceTranslation();
  const { currentRole, isLoggedIn, auth } = useAuthenticationContext();
  const isAdmin = currentRole?.name === GEOWEB_ROLE_PRESETS_ADMIN.name;

  const activeViewPresetId = useSelector((store: AppStore) =>
    viewPresetSelectors.getViewPresetActiveId(store, panelId),
  );

  // whole workspace (id/title/abstract/scope + views)
  const currentWorkspace = useSelector((store: AppStore) =>
    getWorkspaceState(store),
  );

  const thisView = useSelector((store: AppStore) => {
    const { title, scope, ...restProps } = getViewById(store, panelId) ?? {};
    return { title, scope, ...restProps };
  }, shallowEqual);

  const workspaceScope = currentWorkspace?.scope || 'user';
  const workspaceOwner = currentWorkspace?.username;
  const currentViewScope = thisView?.scope || 'user';
  const viewpresetOwner = thisView?.username;

  const isWorkspaceOwner =
    !isAdmin &&
    ((!!workspaceOwner && auth?.username === workspaceOwner) ||
      (!workspaceOwner && workspaceScope === 'user' && isLoggedIn));

  const isUserPresetCreator =
    !isAdmin && !!auth?.username && auth.username === viewpresetOwner;

  const viewComponentType = useSelector((store: AppStore) =>
    getViewComponentType(store, panelId),
  );
  const hasViewPresetChanges = useSelector((store: AppStore) =>
    viewPresetSelectors.getViewPresetHasChanges(store, panelId),
  );

  const relatedSyncGroupIds = useSelector((store: AppStore) =>
    syncGroupsSelectors.getAllTargetGroupsForSource(store, panelId),
  );

  // Check if viewpreset is inline (plain) or comes from a viewpreset (has a activeViewPresetId)
  const hasActiveViewPresetId = activeViewPresetId?.trim() !== '';
  const isPresetBacked =
    hasActiveViewPresetId &&
    ((activeViewPresetId && activeViewPresetId !== panelId) ||
      (thisView?.id && thisView?.id !== thisView?.mosaicNodeId));

  const isPlainWorkspaceView = !isPresetBacked;

  // Regular viewPresets, stored in the db
  const isPresetActionsEnabled =
    isLoggedIn &&
    isPresetBacked &&
    ((currentViewScope === 'user' && isUserPresetCreator) ||
      (currentViewScope === 'system' && isAdmin));

  // Workspace inline views, only allow editing if it's a plain view OR you are the owner of the current workspace
  const canEditWorkspaceViewTitle =
    isLoggedIn &&
    ((workspaceScope === 'user' && isWorkspaceOwner) ||
      (workspaceScope === 'system' && isAdmin));

  React.useEffect(() => {
    if (hasViewPresetChanges) {
      dispatch(workspaceActions.markWorkspaceChanged());
    }
  }, [hasViewPresetChanges, dispatch]);

  const presetAbstract = thisView?.abstract;
  const presetDescription = thisView?.description;
  const presetLabels = thisView?.labels;

  const title = thisView.title || t('workspace-mappreset-new');

  const showChangedInViewTitle = !!hasViewPresetChanges;
  const isNewForDisplay = !hasActiveViewPresetId && !hasViewPresetChanges;

  const fullTitle = getPresetTitle(
    title,
    showChangedInViewTitle,
    isNewForDisplay,
    t,
  );

  // Whether or not to display the panelId next to the preset title
  const showPanelId = constructSyncSourceDisplayName(
    panelId,
    thisView,
    `${title} (${panelId})`,
  ).includes(`(${panelId})`);

  const openViewPresetsDialog = React.useCallback(
    (viewPresetDialogOptions: ViewPresetDialog) => {
      dispatch(
        viewPresetActions.openViewPresetDialog({
          viewPresetDialog: viewPresetDialogOptions,
        }),
      );
    },
    [dispatch],
  );
  const currentWarningsPreset = useSelector((store: WarningModuleStore) =>
    publicWarningsSelectors.getWarningsPresetById(store, panelId),
  );

  const constructInitialProps = React.useCallback(():
    | InitialProps
    | undefined => {
    switch (viewComponentType) {
      case 'WarningList':
      case 'WarningsTimeline':
        return currentWarningsPreset;
      case 'TimeSeries': {
        const state = store.getState();
        const preset = timeSeriesSelectors.getTimeSeriesPreset(
          state as AppStore & TimeSeriesModuleState,
          panelId,
        );
        const isLocationsDialogOpen =
          uiSelectors.getisDialogOpen(
            state,
            uiTypes.DialogTypes.TimeseriesLocations,
          ) &&
          uiSelectors.getSourcePanelId(
            state,
            uiTypes.DialogTypes.TimeseriesLocations,
          ) === panelId;
        return { ...preset, isLocationsDialogOpen };
      }
      case 'Soundings':
        return soundingsSelectors.getSoundingsPreset(
          store.getState() as SoundingsModuleState,
          panelId,
        );
      case 'Map':
        return {
          mapPreset: mapSelectors.getMapPreset(store.getState(), panelId),
          syncGroupsIds: relatedSyncGroupIds,
        };
      case 'Plugin':
        return getViewPresetToSendToBackend(store.getState(), panelId)
          .initialProps;
      default:
        return undefined;
    }
  }, [
    currentWarningsPreset,
    panelId,
    relatedSyncGroupIds,
    store,
    viewComponentType,
  ]);

  const onSave = React.useCallback((): void => {
    dispatch(
      viewPresetActions.saveViewPreset({
        panelId,
        viewPresetId: activeViewPresetId!,
        viewPreset: {
          ...emptyViewPreset(viewComponentType),
          title,
          description: presetDescription,
          abstract: presetAbstract,
          labels: presetLabels,
          scope: currentViewScope,
          initialProps: constructInitialProps(),
        },
      }),
    );
  }, [
    activeViewPresetId,
    constructInitialProps,
    dispatch,
    currentViewScope,
    panelId,
    presetAbstract,
    presetDescription,
    presetLabels,
    title,
    viewComponentType,
  ]);

  const onDelete = React.useCallback((): void => {
    openViewPresetsDialog({
      title: t('workspace-mappreset-dialog-title-delete'),
      action: PresetAction.DELETE,
      viewPresetId: activeViewPresetId!,
      panelId,
      formValues: { title, abstract: presetAbstract },
    });
  }, [
    activeViewPresetId,
    openViewPresetsDialog,
    panelId,
    title,
    presetAbstract,
    t,
  ]);

  const onSaveAs = React.useCallback((): void => {
    const formTitle = activeViewPresetId ? title : '';
    openViewPresetsDialog({
      title: t('workspace-mappreset-save-as'),
      action: PresetAction.SAVE_AS,
      viewPresetId: activeViewPresetId!,
      panelId,
      formValues: {
        title: formTitle,
        description: presetDescription,
        initialProps: constructInitialProps(),
        abstract: presetAbstract,
        labels: presetLabels,
      },
    });
  }, [
    activeViewPresetId,
    constructInitialProps,
    openViewPresetsDialog,
    panelId,
    t,
    title,
    presetDescription,
    presetAbstract,
    presetLabels,
  ]);

  const handleEditTitle = React.useCallback(() => {
    const ws = currentWorkspace;
    const view = thisView;
    if (!ws || !view) {
      return;
    }

    dispatch(
      workspaceListActions.openWorkspaceActionDialogOptions({
        action: WorkspacePresetAction.EDIT_TITLE,
        presetId: ws.id!,
        formValues: {
          title: ws.title ?? '',
          mosaicNode: ws.mosaicNode,
          views: [
            {
              mosaicNodeId: view.mosaicNodeId ?? panelId,
              title: view.title ?? view.mosaicNodeId ?? panelId,
              componentType: view.componentType,
              initialProps: view.initialProps,
            },
          ],
        },
      }),
    );
  }, [currentWorkspace, thisView, panelId, dispatch]);

  const onEdit = React.useCallback((): void => {
    const formTitle = activeViewPresetId ? title : '';
    openViewPresetsDialog({
      title: t('workspace-mappreset-dialog-title-edit'),
      action: PresetAction.EDIT,
      panelId,
      viewPresetId: activeViewPresetId!,
      formValues: {
        title: formTitle,
        description: presetDescription,
        initialProps: constructInitialProps(),
        abstract: presetAbstract,
        labels: presetLabels,
      },
    });
  }, [
    activeViewPresetId,
    title,
    openViewPresetsDialog,
    panelId,
    constructInitialProps,
    presetAbstract,
    presetDescription,
    presetLabels,
    t,
  ]);

  const onReset = React.useCallback((): void => {
    if (isPlainWorkspaceView) {
      const ws = currentWorkspace;
      const view = thisView;
      if (!ws || !view) {
        return;
      }

      dispatch(
        workspaceListActions.openWorkspaceActionDialogOptions({
          action: WorkspacePresetAction.RESET,
          presetId: ws.id!,
          formValues: {
            title: ws.title ?? '',
            mosaicNode: ws.mosaicNode,
            views: [
              {
                mosaicNodeId: view.mosaicNodeId ?? panelId,
                title: view.title ?? view.mosaicNodeId ?? panelId,
                componentType: view.componentType,
                initialProps: view.initialProps,
              },
            ],
          },
        }),
      );
    } else {
      openViewPresetsDialog({
        title: t('workspace-reset-title'),
        action: PresetAction.RESET,
        viewPresetId: activeViewPresetId!,
        panelId,
        formValues: { title },
      });
    }
  }, [
    isPlainWorkspaceView,
    currentWorkspace,
    thisView,
    panelId,
    dispatch,
    openViewPresetsDialog,
    activeViewPresetId,
    title,
    t,
  ]);

  const [isPasteDialogOpen, setIsPasteDialogOpen] = React.useState(false);

  // Retrieve view preset as would be sent to the BE
  const liveViewPreset = useSelector((store: AppStore) =>
    getViewPresetToSendToBackend(store, panelId),
  );

  const onCopyToClipboard = React.useCallback(async (): Promise<void> => {
    try {
      let fullPreset: ViewPreset;
      // inline preset
      if (!hasActiveViewPresetId) {
        fullPreset = { ...liveViewPreset, title } as ViewPreset;
      } else {
        try {
          fullPreset = await getQueryClient().fetchQuery(
            viewpresetQueryOptions.getViewPreset(activeViewPresetId!, auth!),
          );
        } catch {
          if (!liveViewPreset.initialProps) {
            throw new Error('View preset not available');
          }
          fullPreset = liveViewPreset as ViewPreset;
        }
      }
      const sanitized = sanitizeViewPresetForClipboard(fullPreset);
      const json = JSON.stringify(sanitized, null, 2);
      await writeToClipboard(json);
      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
          message: t('workspace-copy-viewpreset-success'),
        }),
      );
    } catch {
      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
          message: t('workspace-error-generic'),
        }),
      );
    }
  }, [
    activeViewPresetId,
    auth,
    dispatch,
    hasActiveViewPresetId,
    liveViewPreset,
    t,
    title,
  ]);

  const onPasteFromClipboard = (): void => {
    setIsPasteDialogOpen(true);
  };

  const onPasteViewPresetConfirm = React.useCallback(
    (jsonString: string): void => {
      const parsed = JSON.parse(jsonString) as ViewPreset;
      const sanitized = sanitizeViewPresetForClipboard(parsed);
      if (sanitized.componentType === 'Map' && sanitized.initialProps) {
        dispatch(
          mapActions.setMapPreset({
            mapId: panelId,
            initialProps:
              sanitized.initialProps as mapTypes.MapPresetInitialProps,
          }),
        );
      }
      dispatch(
        viewPresetActions.fetchedViewPreset({
          viewPreset: sanitized,
          viewPresetId: '',
          panelId,
        }),
      );
      dispatch(
        workspaceActions.updateWorkspaceView({
          mosaicNodeId: panelId,
          viewPreset: sanitized as unknown as WorkspaceViewType,
        }),
      );
      dispatch(workspaceActions.markWorkspaceChanged());
      dispatch(
        snackbarActions.openSnackbar({
          type: snackbarTypes.SnackbarMessageType.VERBATIM_MESSAGE,
          message: t('workspace-paste-viewpreset-success'),
        }),
      );
    },
    [dispatch, panelId, t],
  );

  const toggleViewPresetDialog = React.useCallback(
    ({
      panelId,
      isViewPresetDialogOpen,
    }: ToggleViewPresetDialogProps): void => {
      dispatch(
        viewPresetActions.toggleViewPresetListDialog({
          panelId,
          isViewPresetListDialogOpen: isViewPresetDialogOpen,
        }),
      );
    },
    [dispatch],
  );

  const onClickOpenViewPreset = React.useCallback((): void => {
    toggleViewPresetDialog({
      panelId,
      isViewPresetDialogOpen: !isViewPresetDialogOpen,
    });
  }, [isViewPresetDialogOpen, panelId, toggleViewPresetDialog]);

  renderCounter.count(WorkspaceViewTitleConnectNoMemo.name);

  return (
    // Zero-length string assignment prevents showing title as an empty object '[object Object]'.
    // in child components. This is a workaround for a limitation in the Tooltip component.
    <Grid
      sx={{
        display: 'flex',
        flexGrow: 3,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
      title=""
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexGrow: 1,
          minWidth: 0,
          gap: 1,
        }}
      >
        <CustomTooltip
          slotProps={{
            transition: { exit: false },
          }}
          title={
            !isViewPresetDialogOpen && (
              <>
                <Box
                  component="span"
                  sx={{ fontWeight: 'bold', display: 'block' }}
                >
                  {t('workspace-viewpreset-menu')}
                </Box>
                <Box component="span">{`${t('workspace-currently')}: ${title}`}</Box>
              </>
            )
          }
        >
          <CustomToggleButton
            data-testid="open-viewpresets"
            disableRipple
            onClick={onClickOpenViewPreset}
            selected={isViewPresetDialogOpen}
            sx={{
              minWidth: 0,
              maxWidth: 220,
              overflow: 'hidden',
              '&&': {
                textTransform: 'none',
                backgroundColor: !isViewPresetDialogOpen
                  ? 'transparent'
                  : 'geowebColors.workspace.tabButtonHover',
                '&:hover': {
                  backgroundColor: 'geowebColors.workspace.tabButtonHover',
                },
              },
            }}
            variant="tool"
          >
            <Typography
              sx={{
                fontSize: '12px',
                fontStyle: hasViewPresetChanges ? 'italic' : undefined,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
              }}
            >
              {fullTitle}
              {showPanelId ? (
                <Box
                  component="span"
                  sx={{
                    fontStyle: 'normal',
                    fontWeight: 400,
                    ml: 1,
                    color: isViewPresetDialogOpen
                      ? 'geowebColors.text.inverse'
                      : 'geowebColors.captions.captionStatus.rgba',
                  }}
                >
                  {panelId}
                </Box>
              ) : null}
            </Typography>
          </CustomToggleButton>
        </CustomTooltip>

        <WorkspaceOptions
          copyToClipboardLabel={t('workspace-copy-viewpreset-to-clipboard')}
          handleCopyToClipboard={
            // If stand alone view preset OR inline viewpreset AND logged in
            (isPresetBacked || !hasActiveViewPresetId) && isLoggedIn
              ? onCopyToClipboard
              : undefined
          }
          handleDelete={isPresetActionsEnabled ? onDelete : undefined}
          handleEdit={isPresetActionsEnabled ? onEdit : undefined}
          handleEditTitle={
            canEditWorkspaceViewTitle ? handleEditTitle : undefined
          }
          handlePasteFromClipboard={
            isLoggedIn ? onPasteFromClipboard : undefined
          }
          handleReset={onReset}
          handleSave={isPresetActionsEnabled ? onSave : undefined}
          handleSaveAs={isLoggedIn ? onSaveAs : undefined}
          id={viewPresetOptionId}
          pasteFromClipboardLabel={t(
            'workspace-paste-viewpreset-from-clipboard',
          )}
          showEditTitleOption
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', pt: 0.72 }}>
        <LoadingIndicatorConnect panelId={panelId} />
      </Box>
      <PastePresetDialog
        isOpen={isPasteDialogOpen}
        mode="viewpreset"
        onClose={() => setIsPasteDialogOpen(false)}
        onConfirm={onPasteViewPresetConfirm}
      />
    </Grid>
  );
};

export const WorkspaceViewTitleConnect = React.memo(
  WorkspaceViewTitleConnectNoMemo,
);
